import React, { useMemo } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { CccContractSubmissionRow } from '../../api/cccApi';

const EE_GAS_STATUS_TAG: Record<string, { color: string; bg: string }> = {
  available: { color: '#3b82f6', bg: '#dbeafe' },
  in_progress: { color: '#f59e0b', bg: '#fef3c7' },
  enviado: { color: '#06b6d4', bg: '#e0f2fe' },
  pending: { color: '#8b5cf6', bg: '#ede9fe' },
  objected: { color: '#ec4899', bg: '#fce7f3' },
  installed: { color: '#10b981', bg: '#d1fae5' },
  cancelled: { color: '#ef4444', bg: '#fee2e2' },
};

const formatSubmittedDatePt = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatOptionalDateCell = (v: string | null | undefined) => {
  if (!v) return '—';
  return formatSubmittedDatePt(v) || v;
};

const formatMultiValueCell = (
  row: CccContractSubmissionRow,
  opts: {
    displayKey: 'cpe_display' | 'cui_display';
    listKey: 'cpe_list' | 'cui_list';
    countKey: 'cpe_count' | 'cui_count';
    singleKey: 'cpe' | 'cui';
  }
): React.ReactNode => {
  const display = String(row[opts.displayKey] || '').trim();
  const list = Array.isArray(row[opts.listKey]) ? row[opts.listKey]!.filter(Boolean) : [];
  const count = row[opts.countKey] ?? list.length;
  if (display) {
    if (count > 1) {
      return <span title={list.join('\n')}>{display}</span>;
    }
    return display;
  }
  const single = String(row[opts.singleKey] || '').trim();
  return single || '—';
};

type Props = {
  rows: CccContractSubmissionRow[];
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
};

const CccContractSubmissionsTable: React.FC<Props> = ({ rows, page, pageSize, onPageChange }) => {
  const columns: ColumnsType<CccContractSubmissionRow> = useMemo(
    () => [
      {
        title: 'Data de Submissão',
        dataIndex: 'submitted_at',
        key: 'submitted_at',
        width: 120,
        render: (v) => formatSubmittedDatePt(v),
      },
      {
        title: 'Urgente',
        dataIndex: 'urgente',
        key: 'urgente',
        width: 88,
        align: 'center',
        render: (v: boolean) =>
          v ? (
            <Tag color="red" style={{ margin: 0 }}>
              Urgente
            </Tag>
          ) : (
            '—'
          ),
      },
      {
        title: 'ID Proposta',
        dataIndex: 'id_proposta',
        key: 'id_proposta',
        width: 110,
        render: (v) => v || '—',
      },
      {
        title: 'Nome da Empresa',
        dataIndex: 'company_name',
        key: 'company_name',
        render: (v) => v || '—',
      },
      {
        title: 'NIF',
        dataIndex: 'nif',
        key: 'nif',
        width: 110,
        render: (v) => v || '—',
      },
      {
        title: 'Área CCC',
        dataIndex: 'workspace_label',
        key: 'workspace_label',
        width: 110,
        render: (v) => v || '—',
      },
      {
        title: 'Submetido por (CCC)',
        key: 'submitted_by',
        width: 150,
        render: (_v, r) => {
          const main = (r.submitted_by_name || '').trim() || '—';
          const code = (r.codigo_comercial || '').trim();
          return (
            <span title={code ? `Código: ${code}` : undefined}>
              {main}
              {code ? ` (${code})` : ''}
            </span>
          );
        },
      },
      {
        title: 'Tipo Serviço',
        dataIndex: 'service_type_label',
        key: 'service_type_label',
        width: 100,
        render: (v) => v || 'EE/GN',
      },
      {
        title: 'CPE',
        key: 'cpe',
        width: 120,
        render: (_v, r) =>
          formatMultiValueCell(r, {
            displayKey: 'cpe_display',
            listKey: 'cpe_list',
            countKey: 'cpe_count',
            singleKey: 'cpe',
          }),
      },
      {
        title: 'CUI',
        key: 'cui',
        width: 100,
        render: (_v, r) =>
          formatMultiValueCell(r, {
            displayKey: 'cui_display',
            listKey: 'cui_list',
            countKey: 'cui_count',
            singleKey: 'cui',
          }),
      },
      {
        title: 'Representante',
        dataIndex: 'representative_name',
        key: 'representative_name',
        render: (v) => v || '—',
      },
      {
        title: 'Contacto',
        dataIndex: 'representative_contact',
        key: 'representative_contact',
        width: 120,
        render: (v) => v || '—',
      },
      {
        title: 'BackOffice',
        dataIndex: 'backoffice_claimed_by',
        key: 'backoffice_claimed_by',
        width: 130,
        render: (v) => v || '—',
      },
      {
        title: 'Data de Ativação',
        dataIndex: 'data_ativacao',
        key: 'data_ativacao',
        width: 120,
        render: (v) => formatOptionalDateCell(v),
      },
      {
        title: 'Estado do Contrato',
        dataIndex: 'status',
        key: 'status',
        width: 160,
        render: (_v, r) => {
          const s = EE_GAS_STATUS_TAG[r.status] || { color: '#64748b', bg: '#f1f5f9' };
          return (
            <Tag color={s.color} style={{ background: s.bg, color: s.color, borderColor: s.bg, margin: 0 }}>
              {r.status_label || r.status || '—'}
            </Tag>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="ccc-contract-submissions-table">
      <h3 className="ccc-contract-submissions-title">Submissões de Contrato (EE / Gás)</h3>
      <Table
        rowKey="row_id"
        dataSource={rows}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total: rows.length,
          showSizeChanger: false,
          onChange: onPageChange,
        }}
        size="small"
        scroll={{ x: 1600 }}
      />
    </div>
  );
};

export default CccContractSubmissionsTable;
